/*
 * Trying a skin on (Skins, mockup 36, ENG-737). "Try it on" on a listener's
 * profile card puts the skin they wear on this viewer for the rest of the
 * song: a now-playing card skin draws on this viewer's card (cardSkin.js), a
 * name paint paints this viewer's own chat name (namePaint.js). Only this
 * viewer sees it and nothing is sent to the server; it comes off on "Take it
 * off" or when the playing song changes.
 *
 * The panel's "Get it" buys the skin (a free one, priceCents 0, is got the
 * same way, ENG-827). What it costs and whether any are left
 * come from `GET /public/skins/{skinId}` (backend ENG-738, schema `SkinOffer`):
 *
 *   { skin: SkinsSkin, forSale, priceCents?, editionSize?, editionsIssued,
 *     nextEdition?, soldOut, regularPriceCents?, saleEndsAt?, availableUntil?,
 *     availableFrom? }
 *
 * During a sale (ENG-829) `priceCents` is the sale price, the one charged and
 * sent back as `expected_price_cents`; `regularPriceCents` and `saleEndsAt`
 * come with it and are absent otherwise (normalized as `sale`, ENG-832).
 *
 * A time-limited item (ENG-831/834) carries `availableUntil` (RFC 3339 UTC,
 * an exclusive end) while it is for sale; before its window opens the offer
 * is `forSale:false` with `availableFrom` ("coming soon"). `saleEndsAt` is
 * never later than `availableUntil`.
 *
 * `nextEdition` is only a hint for the label; the purchase's own
 * `edition_number` is the copy the buyer got.
 */
import config from '../config.js';
import { fetchWrapper } from './fetchWrapper.js';
import { normalizeCardSkin } from './listenerCard.js';
import { cardSkinLook, previewForSong } from './cardSkin.js';
import { normalizeNamePaint } from './namePaint.js';

export const NAME_PAINT_SKIN = 'name_paint';

const positiveInt = (value) => {
  const n = Number(value);
  return Number.isSafeInteger(n) && n > 0 ? n : null;
};

/**
 * How a skin is tried on: `look` for a now-playing card skin, `paint` for a
 * name paint; null for a skin that can't be tried on here.
 */
export const tryOnLook = (skin) => {
  if (!skin || typeof skin !== 'object') return null;
  const look = cardSkinLook(skin);
  if (look) return { look, paint: null };
  if (skin.type === NAME_PAINT_SKIN) {
    const paint = normalizeNamePaint(skin.paint);
    if (paint) return { look: null, paint };
  }
  return null;
};

/** Whether a listener card's worn skin can be tried on. */
export const canTryOn = (card) => Boolean(tryOnLook(card?.wearing?.skin));

/**
 * A try-on of `skin` (a normalized `SkinsSkin`) started while `songKey`
 * plays; null when the skin can't be tried on.
 */
export const startTryOn = (skin, songKey) => {
  const how = tryOnLook(skin);
  if (!how) return null;
  return { skin, look: how.look, paint: how.paint, songKey: songKey ?? null };
};

/** The try-on still on while `songKey` plays: null once the song has changed. */
export const tryOnForSong = previewForSong;

/**
 * A price as the backend sent it: a whole number of cents, 0 (free, ENG-825)
 * or more, passed through unchanged; null for anything else. It is sent back
 * as `expected_price_cents`, so it is never computed or defaulted here.
 */
export const offerPriceCents = (value) => (Number.isSafeInteger(value) && value >= 0 ? value : null);

/**
 * An offer's sale (backend ENG-829), checked: `{ regularPriceCents, endsAt }`
 * (`endsAt` the RFC 3339 string as sent, an exclusive end) or null. A sale
 * shows only when the item is for sale, `regularPriceCents` is a whole number
 * of cents above the charged `priceCents`, and `saleEndsAt` parses; anything
 * else reads as no sale, so bad data never shows a strikethrough. The sale
 * price is `priceCents` itself: nothing is computed here.
 */
export const normalizeSale = (input, priceCents) => {
  if (priceCents === null || priceCents === undefined || !input || typeof input !== 'object') return null;
  const regular = input.regularPriceCents;
  if (!Number.isSafeInteger(regular) || regular <= priceCents) return null;
  const endsAt = input.saleEndsAt;
  if (typeof endsAt !== 'string' || !endsAt || Number.isNaN(Date.parse(endsAt))) return null;
  return { regularPriceCents: regular, endsAt };
};

/** Whether an offer or shop item is on sale right now, as the backend last said. */
export const isOnSale = (offer) => Boolean(offer?.sale);

/** An RFC 3339 time as a Date; null when it is missing or not a time. */
export const offerTime = (value) => {
  if (typeof value !== 'string' || !value) return null;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : new Date(ms);
};

/**
 * The `SkinOffer` payload, checked; null when it is not one. A for-sale offer
 * keeps its `priceCents` as sent, 0 included; one with no valid price is not
 * for sale. `sale` is normalizeSale's. `availableUntil` is the end of the
 * item's sale window as a Date (ENG-834), null without one; `availableFrom`
 * (when an item not yet for sale opens: "coming soon") is kept only on an
 * offer that is not for sale.
 */
export const normalizeSkinOffer = (input) => {
  if (!input || typeof input !== 'object') return null;
  const cardSkin = normalizeCardSkin({ skin: input.skin });
  if (!cardSkin) return null;
  const priceCents = input.forSale === true ? offerPriceCents(input.priceCents) : null;
  const forSale = priceCents !== null;
  const editionSize = positiveInt(input.editionSize) ?? cardSkin.skin.editionSize;
  const issued = Number(input.editionsIssued);
  return {
    skin: cardSkin.skin,
    forSale,
    priceCents,
    sale: forSale ? normalizeSale(input, priceCents) : null,
    availableUntil: offerTime(input.availableUntil),
    availableFrom: forSale ? null : offerTime(input.availableFrom),
    editionSize,
    editionsIssued: Number.isSafeInteger(issued) && issued >= 0 ? issued : 0,
    nextEdition: positiveInt(input.nextEdition),
    soldOut: input.soldOut === true,
  };
};

/** Whether "Get it" can buy this offer: for sale, priced (0 = free) and not sold out. */
export const canBuyOffer = (offer) => Boolean(
  offer?.forSale && offerPriceCents(offer.priceCents) !== null && !offer.soldOut,
);

/**
 * At or below this many editions left, the count is shown ("12 left · #489 of
 * 500"); above it only the next edition ("#201 of 500"), so a low number
 * carries the urgency (ENG-833).
 */
export const EDITION_LEFT_THRESHOLD = 50;

const wholeCount = (value) => {
  const n = Number(value);
  return Number.isSafeInteger(n) && n >= 0 ? n : 0;
};

/**
 * How many editions of an offer or shop item are left (ENG-833), the one
 * place this is worked out:
 *
 *   { limited, size, left, nextEdition, soldOut, showLeft }
 *
 * The stock limit is the lifetime cap `editionSize`; `left` is
 * `max(0, editionSize - editionsIssued)`. Sold out is only what the backend
 * says (`soldOut`, or a 409 `skin_sold_out` that re-reads it); a count that
 * reached 0 without it shows no count rather than guessing. `nextEdition` is
 * the backend's hint, else the one after the last issued while any are left.
 * An unlimited item has `limited: false` and null counts. Null for no offer.
 */
export const editionAvailability = (offer) => {
  if (!offer || typeof offer !== 'object') return null;
  const soldOut = offer.soldOut === true;
  const size = positiveInt(offer.editionSize) ?? positiveInt(offer.skin?.editionSize);
  if (!size) return { limited: false, size: null, left: null, nextEdition: null, soldOut, showLeft: false };
  const issued = wholeCount(offer.editionsIssued);
  const left = Math.max(0, size - issued);
  const nextEdition = soldOut || left === 0 ? null : (positiveInt(offer.nextEdition) ?? issued + 1);
  return {
    limited: true,
    size,
    left,
    nextEdition,
    soldOut,
    showLeft: !soldOut && left > 0 && left <= EDITION_LEFT_THRESHOLD,
  };
};

/** Whether an offer is a free skin (priceCents 0). */
export const isFreeOffer = (offer) => Boolean(offer?.forSale && offer.priceCents === 0);

/**
 * Fetches a skin's offer. Rejects with the fetchWrapper error: 404
 * `skin_not_found` for an unknown skin.
 */
export const fetchSkinOffer = async (skinId) => {
  if (typeof skinId !== 'string' || !skinId) return Promise.reject({ status: 400, code: 'invalid_request', message: 'invalid skin id' });
  const data = await fetchWrapper.get(`${config.apiBaseUrl}/public/skins/${encodeURIComponent(skinId)}`);
  const offer = normalizeSkinOffer(data);
  if (!offer) return Promise.reject({ status: 500, code: null, message: 'malformed skin offer' });
  return offer;
};
